import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { signOut } from '@/api/auth';
import { Button } from '@/components/ui/Button';

/**
 * Explicit sign-out. Calls the GraphQL `logout` mutation (the HttpOnly refresh
 * cookie is revoked and cleared server-side), then reloads onto /login so the
 * Apollo cache and every in-memory token are gone. If the server cannot be
 * reached the session is kept and the error is shown: leaving the cookie alive
 * would sign the next page load straight back in.
 */
export function SignOutControls() {
  const [pending, setPending] = useState<'one' | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (everywhere: boolean) => {
    if (
      everywhere &&
      !window.confirm(
        'Sign out everywhere? This ends every session of your account, including this one.',
      )
    ) {
      return;
    }
    setError(null);
    setPending(everywhere ? 'all' : 'one');
    try {
      await signOut({ everywhere });
      window.location.assign('/login');
    } catch {
      setPending(null);
      setError('Could not sign out. Check your connection and try again.');
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center gap-2">
        <Button
          id="admin.profile.signOutEverywhere"
          data-testid="admin.profile.signOutEverywhere"
          variant="ghost"
          size="sm"
          disabled={pending !== null}
          loading={pending === 'all'}
          onClick={() => void run(true)}
        >
          Sign out everywhere
        </Button>
        <Button
          id="admin.profile.signOut"
          data-testid="admin.profile.signOut"
          variant="outline"
          leftIcon={<LogOut className="h-4 w-4" aria-hidden="true" />}
          disabled={pending !== null}
          loading={pending === 'one'}
          onClick={() => void run(false)}
        >
          Sign out
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  );
}
